import { useState, useRef, useEffect } from "react";

export default function useChatDriver({ systemPrompt, selectedCard }) {
  const [lawyerCards, setLawyerCards] = useState(null);
  const [lastIds, setLastIds] = useState([]);
  const [messages, setMessages] = useState([
    { role: "system", content: systemPrompt },
  ]);

  const [input, setInput] = useState("");
  const abortCtrl = useRef(null);

  // Helper to append a message to state
  const pushMessage = (msg) => {
    setMessages((prev) => prev.concat(msg));
  };

  // Debug logger for lawyer cards updates
  useEffect(() => {
    console.log("lawyerCards state updated:", lawyerCards);
  }, [lawyerCards]);

  const sendMessage = async (text) => {
    // Check if this is a refinement request (special case)
    const isRefinementRequest = text.includes('"refinement_request":true');
    console.log("Sending message, refinement:", isRefinementRequest);

    // 1) Cancel any in-flight request
    if (abortCtrl.current) abortCtrl.current.abort();

    // 2) Build the user bubble for the UI
    const userBubble = { role: "user", content: text };
    setMessages((prev) => prev.concat(userBubble));
    setInput("");

    // 3) Prepare what to send to the API
    const firstTurn = messages.length === 1; // only the original system
    const hiddenCardMeta = firstTurn
      ? [
          {
            role: "user",
            content: `__CARD_SELECTED__: ${selectedCard.title} — ${selectedCard.desc}`,
          },
        ]
      : [];

    // strip out any UI‐only messages (cards) before sending
    const apiHistory = [
      ...messages.filter((m) => m.role !== "cards"),
      ...hiddenCardMeta,
      userBubble,
    ];

    // 4) Kick off SSE fetch
    const ctrl = new AbortController();
    abortCtrl.current = ctrl;
    try {
      const res = await fetch("/api/client/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: apiHistory }),
        signal: ctrl.signal,
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // seed an empty assistant bubble so we can stream into it
      pushMessage({ role: "assistant", content: "" });

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.replace(/^data: /, "");
          if (payload === "[DONE]") {
            abortCtrl.current = null;
            return;
          }

          let parsed;
          try {
            parsed = JSON.parse(payload);
          } catch (e) {
            console.error("Error parsing SSE payload:", e, payload);
            continue;
          }

          if (parsed.function_result) {
            // function_result → show cards
            console.log("Received lawyer data:", parsed.function_result);
            pushMessage({ role: "cards", payload: parsed.function_result });
            setLastIds(parsed.function_result.map((r) => r.user_id));
          } else if (parsed.chunk) {
            // streaming assistant text
            setMessages((all) => {
              const copy = all.slice();
              const last = copy[copy.length - 1];
              if (last.role !== "assistant") {
                copy.push({ role: "assistant", content: "" });
              }
              copy[copy.length - 1].content += parsed.chunk;
              return copy;
            });
          } else if (parsed.error) {
            console.error("Error from server:", parsed.error);
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") console.error("Error during SSE stream:", e);
    }
  };

  // Expose everything
  return {
    messages,
    lawyerCards,
    input,
    lastIds,
    setInput,
    sendMessage,
    abortChat: () => {
      if (abortCtrl.current) abortCtrl.current.abort();
      abortCtrl.current = null;
    },
  };
}
